import type { Transition } from 'framer-motion'

/**
 * Motion tokens for PortraitMosaicStrip — docs/plans/portrait-mosaic-design-upgrade.md D6.
 * Single source of truth for every duration/easing/stagger constant this component tree
 * uses: the quote crossfade (S2), the KPI % count-up (S3, moved here by S4), and the tile
 * mount pop-in stagger (S4, new). No inline magic numbers in components — they all import
 * from here. Every consumer still gates on its own `useReducedMotion()` call; these are
 * just the shared constants.
 */

/**
 * Shared ease-out curve. Same curve the crossfade, the count-up, and the tile pop-in all
 * use — the `easings.out` idiom from
 * apps/frontend/src/features/studies/creation/components/goals/motion/tokens.ts.
 */
export const EASE_OUT: [number, number, number, number] = [0.22, 1, 0.36, 1]

/**
 * Quote crossfade (S2): `AnimatePresence`, slot-keyed on the active quote index.
 * `satisfies Transition` (not `: Transition`) so consumers — including tests — keep the
 * literal `duration`/`ease` shape instead of widening to framer's `Transition` union,
 * which does not expose those fields on every variant.
 */
export const QUOTE_CROSSFADE_TRANSITION = {
  duration: 0.4,
  ease: EASE_OUT,
} satisfies Transition

/**
 * Quote rotation timer (S2): ~4s, inside the 3.5-4.5s band the frozen UI spec calls for.
 * Moved here from `PortraitMosaicQuoteBlock.tsx` (fable execution review, finding 5) — every
 * other D6 timing constant already lives in this file; the rotation interval was the one
 * holdout.
 */
export const ROTATION_INTERVAL_MS = 4000

/**
 * Idle window after a reader clicks a tile before auto-rotation takes over again. Long
 * enough to read the verbatim they asked for (the 4-line clamp at ~1.15rem), and roughly
 * four rotation intervals, so a click always reads as "this one stays put for a while".
 */
export const SELECTION_RESUME_DELAY_MS = 15000

/**
 * KPI % count-up (S3). Moved here from `PortraitMosaicKpi.tsx` by S4 per that file's own
 * note — ~0.6s ease-out, same curve as the crossfade.
 */
export const COUNT_UP_DURATION_S = 0.6
export const COUNT_UP_EASE = EASE_OUT

/**
 * Tile mount pop-in (S4, D6): opacity 0 / scale 0.7 -> 1, per the goals/motion/tokens.ts
 * `cardVariants` idiom. `hidden`/`visible` are framer variant states for a tile's
 * `initial`/`animate` props; the per-tile delay comes from {@link tilePopInTransition}.
 */
export const TILE_POP_IN_VARIANTS = {
  hidden: { opacity: 0, scale: 0.7 },
  visible: { opacity: 1, scale: 1 },
} as const

export const TILE_POP_IN_DURATION_S = 0.3

/**
 * ~10ms between one tile's entrance and the next — the low end of the 10-20ms/tile band
 * called for by Step 5b visual QA finding 1 (2026-09-02): the wall must be fully visible
 * in well under a second, never read as "1 tile/second, empty on arrival."
 */
export const TILE_POP_IN_STAGGER_STEP_S = 0.01

/**
 * Past this many tiles, entrances stop staggering further and the remaining tiles pop in
 * together at the capped delay. Without this, a panel sampled up to the 240-tile hard
 * ceiling (`buildPortraitMosaicTiles.ts`) would take several seconds to finish mounting —
 * the plan calls this out by name ("240 tiles do not take forever"). Combined with the
 * step/duration above, the LAST tile is always fully visible by
 * `TILE_POP_IN_STAGGER_CAP * TILE_POP_IN_STAGGER_STEP_S + TILE_POP_IN_DURATION_S` ≈ 0.7s —
 * comfortably inside the ~800ms whole-wall budget regardless of tile count.
 */
export const TILE_POP_IN_STAGGER_CAP = 40

/**
 * Per-tile pop-in transition: fixed duration, delay stepped by index and capped at
 * {@link TILE_POP_IN_STAGGER_CAP}. Negative indices clamp to 0 (defensive only — tile
 * index always comes from `Array.map`). Return type is the literal shape (not the
 * `Transition` union) for the same reason as {@link QUOTE_CROSSFADE_TRANSITION} above —
 * callers can read `.duration`/`.ease`/`.delay` directly; it still satisfies `Transition`
 * everywhere a `transition` prop is expected.
 */
export function tilePopInTransition(
  index: number,
): { duration: number; ease: typeof EASE_OUT; delay: number } {
  const cappedIndex = Math.min(Math.max(index, 0), TILE_POP_IN_STAGGER_CAP)
  return {
    duration: TILE_POP_IN_DURATION_S,
    ease: EASE_OUT,
    delay: cappedIndex * TILE_POP_IN_STAGGER_STEP_S,
  } satisfies Transition
}
